import {
  ShieldCheck, LifeBuoy, Users, Gamepad2, Crown, UserPlus, ClipboardList,
  FileUp, Star, CalendarClock, Activity, Inbox, Mic2, PenLine, Sparkles,
} from "lucide-react";

const ROLES = [
  ["Executive", "Full command — every tab, staff accounts, site text. MFA required.", "text-purple-300 border-purple-500/50"],
  ["Manager", "Day-to-day ops — everything except Team and Site Text.", "text-cyan-300 border-cyan-500/50"],
  ["Artist", "Bookings and Tracks — studio sessions and the beat catalog.", "text-emerald-300 border-emerald-500/50"],
  ["Engineer", "Bookings and Tracks — session schedule and catalog management.", "text-amber-300 border-amber-500/50"],
  ["A&R", "Talent pipeline and Demo Queue — demos, follow-ups, roster approvals.", "text-fuchsia-300 border-fuchsia-500/50"],
];

const TROUBLESHOOTING = [
  ["My email + password work, but it asks for a second code", "That's MFA — it protects the label. Enter the 6-digit code from your authenticator app (executives), or choose the email-code option to have one sent to your sign-in email."],
  ["The email code never arrived", "Wait the full 60-second resend cooldown, check spam/junk for a message from the label, then request a fresh code. Codes expire after 10 minutes and burn after 5 wrong tries — request a new one instead of guessing. Still nothing? Ask your executive to confirm the email on your staff account."],
  ["\"Invalid or expired code\"", "Codes live for 10 minutes and die after 5 failed attempts. Request a new code and enter the latest one — older codes stop working."],
  ["\"Sign-in session expired\"", "For your safety the pre-MFA step times out after 10 minutes. Just log in again from the start."],
  ["I can log in but a tab says Forbidden / won't open", "That's your role. Each role sees only its own tabs (see the role legend). If you need more access, your executive can change your role in the Team tab."],
  ["\"Account deactivated\"", "Your staff account was switched off in the Team tab. Only an executive can reactivate it — contact them directly."],
  ["I forgot my password", "There's no self-service reset yet. Your executive (or the platform owner) deletes and re-creates your staff account in the Team tab with a fresh password."],
  ["Something I changed disappeared after a while", "Sessions expire for security. Save your work before stepping away; if you were mid-edit, log back in and redo the save."],
];

const TABS_TOUR = [
  [Inbox, "Leads", "Every quote request lands here. Reply directly from the card (the artist gets a branded email), move leads through New → Contacted, and Archive the closed ones — the Archived filter keeps them findable."],
  [Mic2, "Talent & Demo Queue", "A&R pipeline: play demos in place, move artists through New → Under Review → Follow Up → A&R Approved (they appear on the public Roster), send one-click branded replies, attach roster photos, and feature the best Snake Pit sends as public Ghost Verses in the Medusa Room."],
  [Gamepad2, "Arcade", "All five Medusa Room leaderboards. Filter by game, remove junk scores, reset one board or all of them, and track prize winners through Won → Claimed → Sent."],
  [Crown, "Medusa", "VIP membership overview — active members, plan, renewal dates, estimated MRR — plus the Vault publisher for members-only drops."],
  [CalendarClock, "Drop Planner", "Schedule which beat sits under the Petrified Countdown on any date. Unscheduled days rotate automatically."],
  [PenLine, "Site Text", "Edit homepage copy, page intros — and the label's public contact details: bookings email, support email, press email, phone numbers, and mailing address. Changes go live on the website immediately."],
  [Sparkles, "AI Writer", "Draft press releases, beat descriptions, emails, and social posts in the label's voice. Review before sending — it's a drafter, not autopilot."],
  [Activity, "Activity", "The audit feed: every staff action across Label Ops, with who did it and when. Check here first when something looks changed."],
];

const HR_GUIDE = [
  [UserPlus, "People directory", "Add every team member with their role, contact details, and a photo. Each person gets a profile card with their onboarding progress bar."],
  [ClipboardList, "Onboarding checklists", "New people start with the default 6-item checklist. Toggle items as they're done and add custom items per person — the progress bar updates live."],
  [FileUp, "Documents & contracts", "Upload contracts, W-9s, and agreements (up to 15 MB) straight onto a person's card. Files are private to Label Ops — they are never publicly reachable."],
  [CalendarClock, "Tasks board", "Create tasks with an owner and watch them move across To Do → In Progress → Done. Use it for session prep, deliverables, and follow-ups."],
  [Star, "Performance reviews", "Log 1–5 star reviews with a period and notes on each person. The history stacks on their card so patterns are visible."],
  [Users, "Interview scheduling", "From the Careers tab, set an interview date/time and notes on any applicant — it persists on their card so the hiring pipeline stays organized."],
];

const Section = ({ id, kicker, title, children }) => (
  <section className="pb-12" data-testid={`ops-guide-${id}`}>
    <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-cyan-400">{kicker}</p>
    <h2 className="font-display text-xl sm:text-2xl font-bold text-white mt-1.5 mb-5">{title}</h2>
    {children}
  </section>
);

export const LabelOpsGuideContent = () => (
  <div data-testid="ops-guide">
    <Section id="signin" kicker="Getting In" title="Signing In, Step by Step">
      <div className="grid sm:grid-cols-3 gap-3">
        {[
          ["1. Get your account", "Staff accounts are created by an executive in the Team tab. You'll receive an email and a temporary password — there is no public signup."],
          ["2. Enter email + password", "At /admin, sign in with those credentials. Passwords are case-sensitive."],
          ["3. Pass the MFA check", "Executives use an authenticator-app code; anyone can also choose Send code by email. Enter the 6-digit code and you're in."],
        ].map(([t, b]) => (
          <div key={t} className="rounded-2xl border border-[#26262e] bg-[#0E0E12]/80 p-5">
            <p className="font-display text-white text-sm font-bold">{t}</p>
            <p className="mt-2 text-xs text-ice/55 leading-relaxed">{b}</p>
          </div>
        ))}
      </div>
    </Section>

    <Section id="troubleshooting" kicker="Login Problems" title="Troubleshooting">
      <div className="space-y-3" data-testid="ops-guide-troubleshooting-list">
        {TROUBLESHOOTING.map(([problem, fix]) => (
          <div key={problem} className="rounded-2xl border border-[#26262e] bg-[#0E0E12]/80 p-5">
            <div className="flex items-start gap-3">
              <LifeBuoy size={16} className="text-amber-300 shrink-0 mt-0.5" />
              <div>
                <p className="font-display text-white text-sm font-bold">{problem}</p>
                <p className="mt-1.5 text-xs text-ice/55 leading-relaxed">{fix}</p>
              </div>
            </div>
          </div>
        ))}
      </div>
    </Section>

    <Section id="roles" kicker="Who Sees What" title="Roles & Permissions">
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="ops-guide-roles-list">
        {ROLES.map(([role, desc, cls]) => (
          <div key={role} className="rounded-2xl border border-[#26262e] bg-[#0E0E12]/80 p-5">
            <span className={`inline-block font-mono text-[10px] uppercase tracking-[0.15em] border rounded-full px-3 py-1 ${cls}`}>{role}</span>
            <p className="mt-2.5 text-xs text-ice/55 leading-relaxed">{desc}</p>
          </div>
        ))}
      </div>
    </Section>

    <Section id="tabs" kicker="The Back Office" title="Tab-by-Tab Tour">
      <div className="grid sm:grid-cols-2 gap-3" data-testid="ops-guide-tabs-list">
        {TABS_TOUR.map(([Icon, name, desc]) => (
          <div key={name} className="rounded-2xl border border-[#26262e] bg-[#0E0E12]/80 p-5">
            <div className="flex items-center gap-2.5 mb-2">
              <Icon size={15} className="text-cyan-300 shrink-0" />
              <p className="font-display text-white text-sm font-bold">{name}</p>
            </div>
            <p className="text-xs text-ice/55 leading-relaxed">{desc}</p>
          </div>
        ))}
      </div>
    </Section>

    <Section id="hr" kicker="People Operations" title="HR Suite User Guide">
      <p className="text-sm text-ice/55 max-w-2xl mb-6 leading-relaxed">
        The HR Suite tab (executives and managers) is the label's people department: who works here, what they're onboarding on, their documents, their tasks, and their reviews.
      </p>
      <div className="grid sm:grid-cols-2 gap-3" data-testid="ops-guide-hr-list">
        {HR_GUIDE.map(([Icon, name, desc]) => (
          <div key={name} className="rounded-2xl border border-purple-500/20 bg-[#0E0E12]/80 p-5">
            <div className="flex items-center gap-2.5 mb-2">
              <Icon size={15} className="text-purple-300 shrink-0" />
              <p className="font-display text-white text-sm font-bold">{name}</p>
            </div>
            <p className="text-xs text-ice/55 leading-relaxed">{desc}</p>
          </div>
        ))}
      </div>
      <div className="mt-6 rounded-2xl border border-amber-500/25 bg-amber-950/10 p-5">
        <div className="flex items-start gap-3">
          <ShieldCheck size={17} className="text-amber-300 shrink-0 mt-0.5" />
          <p className="text-xs text-ice/60 leading-relaxed">
            HR documents are sensitive: they're served only to signed-in staff, never publicly. Keep contracts and personal data inside the HR Suite rather than email threads.
          </p>
        </div>
      </div>
    </Section>
  </div>
);
